import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useAuthStatusContext, useInitialQueryValueContext } from 'contexts';
import { useRequiredContext } from 'hooks';

interface GlobalMemberPopoversContextType {
	closeLogin: () => void;
	closeMenu: () => void;
	closeSignUp: () => void;
	isLoginOpen: boolean;
	isMenuOpen: boolean;
	isSignUpOpen: boolean;
	openLogin: () => void;
	openMenu: () => void;
	openSignUp: () => void;
}
const GlobalMemberPopoversContext = createContext<
	GlobalMemberPopoversContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

export function GlobalMemberPopoversContextProvider({ children }: Props) {
	const { shouldInitialOpenLogin, shouldInitialOpenSignupForm } =
		useAuthStatusContext();
	const { returnTo } = useInitialQueryValueContext();

	const [isSignUpOpen, setIsSignUpOpen] = useState(shouldInitialOpenSignupForm);
	const [isLoginOpen, setIsLoginOpen] = useState(
		shouldInitialOpenLogin || Boolean(returnTo),
	);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const openSignUp = useCallback(() => {
		setIsSignUpOpen(true);
	}, []);
	const closeSignUp = useCallback(() => {
		setIsSignUpOpen(false);
	}, []);

	const openLogin = useCallback(() => {
		setIsLoginOpen(true);
	}, []);
	const closeLogin = useCallback(() => {
		setIsLoginOpen(false);
	}, []);

	const openMenu = useCallback(() => {
		setIsMenuOpen(true);
	}, []);
	const closeMenu = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	const value = useMemo(
		() => ({
			closeLogin,
			closeMenu,
			closeSignUp,
			isLoginOpen,
			isMenuOpen,
			isSignUpOpen,
			openMenu,
			openSignUp,
			openLogin,
		}),
		[
			openLogin,
			closeLogin,
			closeMenu,
			closeSignUp,
			isLoginOpen,
			isMenuOpen,
			isSignUpOpen,
			openMenu,
			openSignUp,
		],
	);
	return (
		<GlobalMemberPopoversContext value={value}>
			{children}
		</GlobalMemberPopoversContext>
	);
}
GlobalMemberPopoversContextProvider.displayName =
	'GlobalMemberPopoversContextProvider';

export function useGlobalMemberPopoversContext() {
	return useRequiredContext(
		GlobalMemberPopoversContext,
		'GlobalMemberPopoversContext',
	);
}
